import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface InventoryValuationItem {
  sku: string;
  name: string;
  quantity: number;
  unit_cost: number;
  total_value: number;
  category?: string | null;
}

interface ApiInventoryValuation {
  items: {
    sku: string;
    name: string;
    quantity: string;
    unit_cost: string;
    total_value: string;
    category?: string | null;
  }[];
  total_value: string;
  as_of: string;
  currency: string;
}

export function InventoryValuationPage() {
  const { idToken } = useCognitoAuth();
  const [valuation, setValuation] = useState<{
    items: InventoryValuationItem[];
    total_value: number;
    as_of: string;
    currency: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!idToken) return;
    async function fetchData() {
      try {
        const data = await api.get<ApiInventoryValuation>('/api/reports/inventory-valuation', { errorCode: ErrorCode.FETCH_INVENTORY_VALUATION, token: idToken! });
        setValuation({
          items: data.items.map(item => ({
            sku: item.sku,
            name: item.name,
            quantity: Number(item.quantity),
            unit_cost: Number(item.unit_cost),
            total_value: Number(item.total_value),
            category: item.category ?? undefined,
          })),
          total_value: Number(data.total_value),
          as_of: data.as_of,
          currency: data.currency,
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Unable to load inventory valuation');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchData();
  }, [retryKey, idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading inventory valuation...</p>
      </div>
    );
  }

  if (error || !valuation) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error ?? 'Unable to load inventory valuation'}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  const groupedItems = valuation.items.reduce((acc, item) => {
    const category = item.category ?? 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, InventoryValuationItem[]>);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inventory Valuation</h1>
          <p className="text-sm text-muted-foreground">
            As of {valuation.as_of ?? 'Today'}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <i className="bi bi-download mr-2"></i>
            Export CSV
          </Button>
          <Button variant="default" size="sm">
            <i className="bi bi-printer mr-2"></i>
            Print
          </Button>
        </div>
      </div>

      <Card className="bg-muted/50 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Inventory Value</p>
              <p className="text-3xl font-bold font-mono text-primary">
                {formatCurrency(valuation.total_value)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Items</p>
              <p className="text-2xl font-bold">{valuation.items.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {Object.entries(groupedItems).map(([category, items]) => {
        const categoryValue = items.reduce((sum, item) => sum + item.total_value, 0);
        
        return (
          <Card key={category}>
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <CardTitle className="flex items-center gap-2">
                  <i className="bi bi-folder text-muted-foreground"></i>
                  {category}
                </CardTitle>
                <Badge variant="secondary" className="font-mono">
                  {formatCurrency(categoryValue)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">SKU</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Name</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Qty</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Unit Cost</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.sku} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{item.sku}</code>
                        </td>
                        <td className="py-2 px-3">{item.name}</td>
                        <td className="py-2 px-3 text-right font-mono">{item.quantity}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatCurrency(item.unit_cost)}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold">
                          {formatCurrency(item.total_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {valuation.items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <i className="bi bi-box-seam text-4xl text-muted-foreground mb-4"></i>
            <p className="text-muted-foreground">No inventory items found</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
